import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ListChecks, Plus, Search, SquareKanban } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useTasks } from "@/api/queries/tasks"
import { PageContainer, PageHeader } from "@/components/app/page"
import { EmptyState, ErrorState } from "@/components/app/states"
import { Button } from "@/components/ui/button"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group"
import { Skeleton } from "@/components/ui/skeleton"
import { useDebounced } from "@/features/studio/use-debounced"
import { TaskBoard } from "@/features/tasks/task-board"
import { TaskSheet } from "@/features/tasks/task-detail"
import {
  hasTaskFilters,
  LiveIndicator,
  parseTaskFilterSearch,
  TaskFilterControls,
  type TaskFilterSearch,
  toTaskFilters,
} from "@/features/tasks/task-filters"
import { TaskCreateSheet } from "@/features/tasks/task-form"
import { useBoardMove } from "@/features/tasks/use-board-move"
import { useTaskBoardEvents } from "@/features/tasks/use-task-events"

interface BoardSearch extends Omit<TaskFilterSearch, "status" | "sort"> {
  task?: number
}

export const Route = createFileRoute("/_app/tasks/board")({
  staticData: { titleKey: "nav.items.board" },
  validateSearch: (s: Record<string, unknown>): BoardSearch => {
    const { status: _s, sort: _o, ...filters } = parseTaskFilterSearch(s)
    const task = Number(s.task)
    return { ...filters, task: Number.isInteger(task) && task > 0 ? task : undefined }
  },
  component: BoardPage,
})

/** The board loads every matching task (the server caps a page at 2000). */
const BOARD_LIMIT = 2000

function BoardSkeleton() {
  return (
    <div className="flex gap-3 overflow-hidden" aria-hidden>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="bg-muted/40 w-72 shrink-0 space-y-2 rounded-xl border p-2">
          <Skeleton className="h-4 w-24" />
          {Array.from({ length: 3 - (i % 2) }).map((_, j) => (
            <Skeleton key={j} className="h-20" />
          ))}
        </div>
      ))}
    </div>
  )
}

function BoardPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const live = useTaskBoardEvents()
  const move = useBoardMove()
  const [creating, setCreating] = useState(false)
  const [query, setQuery] = useState(search.q ?? "")
  const debounced = useDebounced(query, 300)

  const setSearch = (patch: Partial<BoardSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to the debounced value
  useEffect(() => {
    if ((debounced || undefined) !== search.q) setSearch({ q: debounced || undefined })
  }, [debounced])

  const tasks = useTasks(
    { ...toTaskFilters(search), order_by: "sort_order", limit: BOARD_LIMIT },
    { refetchInterval: live !== "open" ? 10_000 : false },
  )
  const filtered = hasTaskFilters(search)
  const items = tasks.data?.items ?? []

  return (
    <PageContainer width="full">
      <PageHeader
        title={t("tasks.board.title")}
        description={t("tasks.board.description")}
        icon={SquareKanban}
        actions={
          <>
            <Button variant="outline" nativeButton={false} render={<Link to="/tasks" search={{}} />}>
              <ListChecks />
              {t("tasks.board.openList")}
            </Button>
            <Button onClick={() => setCreating(true)} data-testid="new-task">
              <Plus />
              {t("tasks.hub.new")}
            </Button>
          </>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <InputGroup className="h-8 w-full sm:w-56">
          <InputGroupAddon>
            <Search />
          </InputGroupAddon>
          <InputGroupInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("tasks.hub.search")}
            aria-label={t("tasks.hub.search")}
          />
        </InputGroup>
        <TaskFilterControls value={search} onChange={setSearch} showStatus={false} showSort={false} />
        {filtered && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setQuery("")
              setSearch({ q: undefined, project: undefined, assignee: undefined, priority: undefined })
            }}
          >
            {t("tasks.hub.filters.clear")}
          </Button>
        )}
        <div className="ml-auto flex items-center gap-3">
          {tasks.data?.total != null && tasks.data.total > items.length && (
            <span className="text-muted-foreground text-xs">
              {t("tasks.board.truncated", { shown: items.length, total: tasks.data.total })}
            </span>
          )}
          <LiveIndicator status={live} />
        </div>
      </div>
      {tasks.isPending ? (
        <BoardSkeleton />
      ) : tasks.error ? (
        <ErrorState error={tasks.error} onRetry={() => void tasks.refetch()} retrying={tasks.isFetching} />
      ) : items.length === 0 && !filtered ? (
        <EmptyState
          icon={SquareKanban}
          title={t("tasks.hub.empty.title")}
          description={t("tasks.hub.empty.description")}
          action={
            <Button onClick={() => setCreating(true)}>
              <Plus />
              {t("tasks.hub.new")}
            </Button>
          }
        />
      ) : (
        <>
          {items.length === 0 && (
            <p className="text-muted-foreground mb-3 text-sm" role="status">
              {t("tasks.hub.emptyFiltered.description")}
            </p>
          )}
          <TaskBoard
            tasks={items}
            activeTaskId={search.task}
            onOpen={(id) => setSearch({ task: id })}
            onOpenPage={(id) => void navigate({ to: "/tasks/$taskId", params: { taskId: String(id) } })}
            onMove={(m) => void move(m)}
            onCreate={() => setCreating(true)}
          />
        </>
      )}
      <TaskSheet
        taskId={search.task}
        onClose={() => setSearch({ task: undefined })}
        onOpenTask={(id) => setSearch({ task: id })}
      />
      <TaskCreateSheet
        open={creating}
        onOpenChange={setCreating}
        defaults={{ project_id: typeof search.project === "number" ? search.project : null }}
        onCreated={(task) => setSearch({ task: task.id })}
      />
    </PageContainer>
  )
}
